// features/workstations/components/dashboard-focus-mode.js
// ═══════════════════════════════════════════════════════════
// FOCUS MODE — alternative dashboard layout per Tim's spec
// (Commit 74)
// ═══════════════════════════════════════════════════════════
// Layout:
//   ┌──────────────────────────────────────────┬─────────────┐
//   │  ◀ [tab1] [tab2] [● ACTIVE ●] ... ▶ 🔍   │             │
//   ├──────────────────────────────────────────┤  JARVIS     │
//   │                                           │  CHAT       │
//   │  HERO WIDGET (single, full focus)         │  (sticky    │
//   │                                           │   right)    │
//   │                                           │             │
//   └──────────────────────────────────────────┴─────────────┘
//
// Navigation:
//   - Click any tab → swaps into hero
//   - Mouse wheel / trackpad horizontal swipe → scrolls strip
//   - Arrow keys ← → → step through widgets
//   - / key → opens search overlay
//   - Active tab auto-centers in strip when changed
//
// State:
//   - activeWidgetKey: which widget is in the hero
//   - searchOpen: search overlay visibility
//   - searchQuery: filter text
//
// Persists active widget per-workstation in localStorage so
// refresh restores the same hero.
// ═══════════════════════════════════════════════════════════

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { getSectionComponent } from '../dashboard-sections/registry';
import { getWidget, widgetGlyph } from '../../../lib/widgets/registry';
import { AiNameContext } from '../shared/shift-read';
import WidgetStatusDot from '../../../components/widgets/widget-status-dot';

// Keys to exclude from the Focus-mode strip. These duplicate the
// Focus-mode chat panel itself (a dedicated Jarvis surface lives on
// the right side of the focus layout, so an extra chat widget in the
// strip is redundant).
const FOCUS_STRIP_EXCLUDE = new Set([
  'strategic_dialogue',
]);

export default function DashboardFocusMode({
  rows,
  library,
  workstation,
  userId,
  userAiName,
}) {
  // Build the strip from the FULL domain library when available —
  // user's added widgets (active) + domain-suitable widgets they
  // haven't added yet (available + coming_soon). Falls back to
  // flattening `rows` when library isn't provided.
  //
  // Each entry carries a `status`:
  //   'active'       — on user's dashboard
  //   'available'    — built + suitable, user can use it
  //   'coming_soon'  — suitable but not built yet (placeholder renders)
  //
  // Order: user's active widgets first (preserves their arrangement),
  // then the rest of the domain library in registry order.
  const allWidgets = useMemo(() => {
    if (library && (library.active || library.available || library.coming_soon)) {
      const out = [];
      const seen = new Set();
      const pushGroup = (arr, fallbackStatus) => {
        for (const w of (arr || [])) {
          if (!w?.key || seen.has(w.key)) continue;
          if (FOCUS_STRIP_EXCLUDE.has(w.key)) continue;
          seen.add(w.key);
          out.push({ key: w.key, meta: w, status: w.status || fallbackStatus });
        }
      };
      pushGroup(library.active, 'active');
      pushGroup(library.available, 'available');
      pushGroup(library.coming_soon, 'coming_soon');
      return out;
    }
    // Fallback: no library → flatten rows
    const flat = [];
    for (const row of (rows || [])) {
      for (const w of (row.widgets || [])) {
        if (FOCUS_STRIP_EXCLUDE.has(w.key)) continue;
        const meta = getWidget(w.key);
        if (!meta) continue;
        flat.push({ key: w.key, meta, status: 'active' });
      }
    }
    return flat;
  }, [library, rows]);

  // Active widget — persisted per workstation
  const lsKey = `shift_dashboard_focus_widget:${workstation?.id || 'default'}`;
  const [activeKey, setActiveKey] = useState(() => {
    if (typeof window === 'undefined') return null;
    const stored = window.localStorage.getItem(lsKey);
    return stored || null;
  });

  // Default to first widget if no stored or stored is invalid
  useEffect(() => {
    if (allWidgets.length === 0) return;
    if (!activeKey || !allWidgets.find((w) => w.key === activeKey)) {
      const first = allWidgets[0].key;
      setActiveKey(first);
      try { window.localStorage.setItem(lsKey, first); } catch { /* ignore */ }
    }
  }, [allWidgets, activeKey, lsKey]);

  const setActive = useCallback((key) => {
    setActiveKey(key);
    if (typeof window !== 'undefined') {
      try { window.localStorage.setItem(lsKey, key); } catch { /* ignore */ }
    }
  }, [lsKey]);

  // ─── Search overlay ─────────────────────────────────────
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  // ─── Keyboard shortcuts ─────────────────────────────────
  useEffect(() => {
    function handleKey(e) {
      // Don't intercept if user is typing in a field
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') {
        // Except: Escape closes search
        if (e.key === 'Escape' && searchOpen) {
          setSearchOpen(false);
          setSearchQuery('');
        }
        return;
      }

      if (e.key === '/') {
        e.preventDefault();
        setSearchOpen(true);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        const idx = allWidgets.findIndex((w) => w.key === activeKey);
        if (idx >= 0 && idx < allWidgets.length - 1) {
          setActive(allWidgets[idx + 1].key);
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const idx = allWidgets.findIndex((w) => w.key === activeKey);
        if (idx > 0) {
          setActive(allWidgets[idx - 1].key);
        }
      } else if (e.key === 'Escape' && searchOpen) {
        setSearchOpen(false);
        setSearchQuery('');
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [allWidgets, activeKey, setActive, searchOpen]);

  // ─── Auto-center active tab in strip ────────────────────
  const stripRef = useRef(null);
  useEffect(() => {
    if (!stripRef.current || !activeKey) return;
    const activeEl = stripRef.current.querySelector(`[data-widget-key="${activeKey}"]`);
    if (activeEl) {
      activeEl.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }
  }, [activeKey]);

  // ─── Mouse wheel scrolls strip horizontally ─────────────
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    function onWheel(e) {
      // Only convert vertical wheel to horizontal scroll if not also shift-held
      if (e.deltaY !== 0 && Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    }
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // ─── Search filter ──────────────────────────────────────
  const filteredWidgets = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return allWidgets;
    return allWidgets.filter((w) => {
      const name = (w.meta?.name || w.key).toLowerCase();
      const cat = (w.meta?.category || '').toLowerCase();
      return name.includes(q) || cat.includes(q);
    });
  }, [allWidgets, searchQuery]);

  if (allWidgets.length === 0) {
    return (
      <div className="ws-focus-empty">
        No widgets yet. Switch to Grid mode to add some.
      </div>
    );
  }

  const activeMeta = allWidgets.find((w) => w.key === activeKey)?.meta;
  const ActiveSection = activeKey ? getSectionComponent(activeKey) : null;
  const resolvedAiName = (workstation?.ai_name && String(workstation.ai_name).trim())
    || (userAiName && String(userAiName).trim())
    || null;

  const activeIdx = allWidgets.findIndex((w) => w.key === activeKey);

  return (
    <div className="ws-focus-mode">
      {/* ─── Top: tab strip + search button ─── */}
      <div className="ws-focus-strip-wrap">
        <button
          type="button"
          className="ws-focus-strip-arrow"
          onClick={() => {
            if (activeIdx > 0) setActive(allWidgets[activeIdx - 1].key);
          }}
          disabled={activeIdx <= 0}
          aria-label="Previous widget"
        >
          ◀
        </button>

        <div className="ws-focus-strip" ref={stripRef}>
          {allWidgets.map((w) => {
            const isActive = w.key === activeKey;
            // Strip status reflects the library bucket:
            //   active     → on dashboard (full color)
            //   available  → domain-built, not on user's dashboard yet
            //   coming_soon → placeholder
            const stripStatus = w.status || (w.meta?.built ? 'live' : 'empty');
            const dotStatus = w.meta?.built ? 'live' : 'empty';
            const glyph = widgetGlyph(w.meta);
            return (
              <button
                key={w.key}
                type="button"
                data-widget-key={w.key}
                className={`ws-focus-tab ${isActive ? 'is-active' : ''} is-${stripStatus}`}
                onClick={() => setActive(w.key)}
                title={
                  stripStatus === 'coming_soon'
                    ? `${w.meta?.name || w.key} — coming soon`
                    : stripStatus === 'available'
                      ? `${w.meta?.name || w.key} — not on your dashboard yet`
                      : (w.meta?.name || w.key)
                }
              >
                <span className="ws-focus-tab-glyph">{glyph || '◆'}</span>
                <span className="ws-focus-tab-name">
                  {w.meta?.name || w.key}
                </span>
                <WidgetStatusDot status={dotStatus} label="" />
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="ws-focus-strip-arrow"
          onClick={() => {
            if (activeIdx >= 0 && activeIdx < allWidgets.length - 1) {
              setActive(allWidgets[activeIdx + 1].key);
            }
          }}
          disabled={activeIdx >= allWidgets.length - 1}
          aria-label="Next widget"
        >
          ▶
        </button>

        <button
          type="button"
          className="ws-focus-search-btn"
          onClick={() => setSearchOpen(true)}
          title="Search widgets ( / )"
          aria-label="Search widgets"
        >
          🔍
        </button>
      </div>

      {/* Position indicator */}
      <div className="ws-focus-position">
        {activeIdx + 1} of {allWidgets.length}
        {activeMeta && <> · <strong>{activeMeta.name}</strong></>}
        <span className="ws-focus-hints">  ←/→ to navigate · / to search</span>
      </div>

      {/* ─── Body: 60/40 split — hero + chat ─── */}
      <div className="ws-focus-body">
        <div className="ws-focus-hero">
          {ActiveSection ? (
            <AiNameContext.Provider value={resolvedAiName}>
              <ActiveSection
                workstation={workstation}
                userId={userId}
                sectionKey={activeKey}
              />
            </AiNameContext.Provider>
          ) : (
            <div className="ws-focus-hero-empty">
              No widget selected.
            </div>
          )}
        </div>

        <div className="ws-focus-chat">
          <FocusChatPanel
            workstation={workstation}
            userId={userId}
            activeWidgetName={activeMeta?.name || activeKey}
            activeAiName={resolvedAiName}
          />
        </div>
      </div>

      {/* ─── Search overlay ─── */}
      {searchOpen && (
        <div
          className="ws-focus-search-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSearchOpen(false);
              setSearchQuery('');
            }
          }}
        >
          <div className="ws-focus-search-modal">
            <input
              ref={searchInputRef}
              type="text"
              className="ws-focus-search-input"
              placeholder="Search widgets… (Esc to close)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && filteredWidgets.length > 0) {
                  setActive(filteredWidgets[0].key);
                  setSearchOpen(false);
                  setSearchQuery('');
                }
              }}
            />
            <div className="ws-focus-search-results">
              {filteredWidgets.length === 0 && (
                <div className="ws-focus-search-empty">No widgets match.</div>
              )}
              {filteredWidgets.map((w) => {
                const status = w.meta?.status || (w.meta?.built ? 'live' : 'empty');
                return (
                  <button
                    key={w.key}
                    type="button"
                    className="ws-focus-search-result"
                    onClick={() => {
                      setActive(w.key);
                      setSearchOpen(false);
                      setSearchQuery('');
                    }}
                  >
                    <WidgetStatusDot status={status} label="" />
                    <span>{w.meta?.name || w.key}</span>
                    <span className="ws-focus-search-cat">{w.meta?.category}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Simple Jarvis chat panel scoped to current widget ─────
// MVP: just a textarea + history. Later can wire to /api/jarvis/chat
// with widget context injected. For now, posts to the existing
// jarvis briefing chat endpoint or shows placeholder messages.
function FocusChatPanel({ workstation, userId, activeWidgetName, activeAiName }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  // Commit 78 — never fall back to 'Jarvis' in UI. That's internal
  // engine terminology. Fall back to 'Shift' (the product) so the
  // label is always something the user recognizes.
  const aiLabel = activeAiName ? activeAiName : 'Shift';

  async function handleSend() {
    const q = input.trim();
    if (!q || sending) return;
    setSending(true);
    setMessages((m) => [...m, { role: 'user', content: q }]);
    setInput('');

    // MVP: placeholder response. Real integration with
    // /api/jarvis/briefing or a new /api/jarvis/chat endpoint
    // can land in a follow-up commit.
    setTimeout(() => {
      setMessages((m) => [...m, {
        role: 'assistant',
        content: `Got it. (Chat backend wiring lands next commit — for now I'm capturing your question about "${activeWidgetName}". The conversation will reload with full corpus context once /api/jarvis/chat is wired.)`,
      }]);
      setSending(false);
    }, 600);
  }

  return (
    <div className="ws-focus-chat-inner">
      <div className="ws-focus-chat-header">
        <div className="ws-focus-chat-title">{aiLabel}</div>
        <div className="ws-focus-chat-context">
          re: <strong>{activeWidgetName}</strong>
        </div>
      </div>

      <div className="ws-focus-chat-messages">
        {messages.length === 0 && (
          <div className="ws-focus-chat-empty">
            Ask anything about <strong>{activeWidgetName}</strong> or any other
            widget. I have full context across your workstation.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`ws-focus-chat-msg is-${m.role}`}>
            <div className="ws-focus-chat-msg-role">{m.role === 'user' ? 'you' : aiLabel.toLowerCase()}</div>
            <div className="ws-focus-chat-msg-body">{m.content}</div>
          </div>
        ))}
      </div>

      <div className="ws-focus-chat-input-row">
        <textarea
          className="ws-focus-chat-input"
          placeholder="Ask anything…  (Enter to send, Shift+Enter for newline)"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          rows={2}
          disabled={sending}
        />
        <button
          type="button"
          className="ws-focus-chat-send"
          onClick={handleSend}
          disabled={!input.trim() || sending}
        >
          {sending ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
}
